import React from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { Document, Page, pdfjs } from 'react-pdf';
import { Card, CardContent, Divider, Grid, IconButton, LinearProgress, Typography } from '@mui/material';

import { type FilesViewerProps } from '../FilesViewer';
import Icon from '../../Icon';
import { formatPercentage } from '../../Utils';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PdfPaginationProps {
	pageNumber: number;
	setPageNumber: React.Dispatch<React.SetStateAction<number>>;
	nPages: number;
	scale: number;
	setScale: React.Dispatch<React.SetStateAction<number>>;
}

function PdfPagination({ pageNumber, setPageNumber, nPages, scale, setScale }: PdfPaginationProps) {
	function changePage(offset) {
		setPageNumber(prevPageNumber => prevPageNumber + offset);
	}

	function changeScale(offset) {
		setScale(prevScale => prevScale + offset);
	}

	return (
		<div className="p-8">
			<Grid container justifyContent="space-between" alignItems="center" spacing={1}>
				<Grid>
					<Grid container alignItems="center" spacing={1}>
						<Grid>
							<IconButton size="small" disabled={scale <= 0.25} onClick={() => changeScale(-0.25)}>
								<Icon icon="zoom-out" />
							</IconButton>
							<IconButton size="small" disabled={scale >= 2} onClick={() => changeScale(0.25)}>
								<Icon icon="zoom-in" />
							</IconButton>
						</Grid>
						<Grid>
							<Typography variant="subtitle2">{formatPercentage(scale * 100)}</Typography>
						</Grid>
					</Grid>
				</Grid>
				<Grid>
					<Grid container alignItems="center" spacing={1}>
						<Grid>
							<IconButton size="small" disabled={pageNumber <= 1} onClick={() => setPageNumber(1)}>
								<Icon icon="page-first" />
							</IconButton>
							<IconButton size="small" disabled={pageNumber <= 1} onClick={() => changePage(-1)}>
								<Icon icon="chevron-left" />
							</IconButton>
						</Grid>
						<Grid>
							<Typography variant="subtitle2">
								{pageNumber || (nPages ? 1 : '--')} / {nPages || '--'}
							</Typography>
						</Grid>
						<Grid>
							<IconButton size="small" disabled={pageNumber >= nPages} onClick={() => changePage(1)}>
								<Icon icon="chevron-right" />
							</IconButton>
							<IconButton
								size="small"
								disabled={pageNumber >= nPages}
								onClick={() => setPageNumber(nPages)}
							>
								<Icon icon="page-last" />
							</IconButton>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</div>
	);
}

/** This component can display PDFs */
export default function PdfViewer({ fileEncoded }: FilesViewerProps) {
	const [nPages, setNPages] = React.useState(0);
	const [pageNumber, setPageNumber] = React.useState(1);
	const [scale, setScale] = React.useState(1);

	const { ref, width = 0 } = useResizeDetector<HTMLDivElement>({});

	function onDocumentLoadSuccess({ numPages }) {
		setNPages(numPages);
		setPageNumber(1);
	}

	return (
		<Card className="max-h-full rounded-8" variant="outlined">
			<CardContent ref={ref} className="p-0 last:pb-0">
				<PdfPagination
					pageNumber={pageNumber}
					setPageNumber={setPageNumber}
					nPages={nPages}
					scale={scale}
					setScale={setScale}
				/>
				<Divider />
				<Document
					className="h-full w-full overflow-x-auto"
					file={`data:application/pdf;base64,${fileEncoded}`}
					onLoadSuccess={onDocumentLoadSuccess}
					loading={<LinearProgress />}
					onItemClick={({ pageNumber: page }) => setPageNumber(page)}
				>
					<Page
						pageNumber={pageNumber}
						width={width}
						scale={scale}
						loading={<LinearProgress />}
						renderAnnotationLayer={false}
						renderTextLayer={false}
					/>
				</Document>
			</CardContent>
		</Card>
	);
}
